import { getSessionId } from '@/lib/analytics';

/**
 * One place reads the API's build-time configuration, so the params adapter and
 * the history client cannot drift on how they authenticate.
 *
 * Both variables are build-time (`import.meta.env`), so changing either on Render
 * requires a redeploy rather than a restart.
 */

/** Empty means "no API": the app runs fully local against localStorage. */
export const API_BASE_URL = import.meta.env['VITE_API_BASE_URL']?.trim() ?? '';

/**
 * Shared key for writes.
 *
 * This ships inside a public Vite bundle, so it is a speed bump against
 * drive-by writes rather than a secret. What makes that acceptable is that the
 * service holds zero PHI — only synthetic aggregate assumptions.
 */
export const API_KEY = import.meta.env['VITE_API_KEY']?.trim() ?? '';

/** Trailing slashes removed, so callers can append a path unconditionally. */
export function apiBase(): string {
  return API_BASE_URL.replace(/\/+$/, '');
}

/**
 * Headers for a normal write. The session id links this write to its PostHog
 * replay, which is what turns a history entry into "watch what happened here".
 */
export function writeHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (API_KEY) headers['X-Api-Key'] = API_KEY;
  const session = getSessionId();
  if (session) headers['X-Session-Id'] = session;
  return headers;
}

/**
 * The same credentials as query parameters, for the one request that cannot set
 * headers: `navigator.sendBeacon` on pagehide.
 */
export function authQuery(): string {
  const query = new URLSearchParams();
  if (API_KEY) query.set('k', API_KEY);
  const session = getSessionId();
  if (session) query.set('s', session);
  return query.size > 0 ? `?${query.toString()}` : '';
}
